import Box from "@material-ui/core/esm/Box";
import Dialog from "@material-ui/core/esm/Dialog";
import DialogTitle from "@material-ui/core/esm/DialogTitle";
import DialogActions from "@material-ui/core/esm/DialogActions";
import DialogContent from "@material-ui/core/esm/DialogContent";
import TextField from "@material-ui/core/esm/TextField";
import CloseIcon from '@material-ui/icons/Close';
import axios from 'axios';
import { nanoid } from 'nanoid';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CustomerService } from 'api';
import { PrimaryButton, SecondaryButton } from 'components/Button';
import Divider from 'components/Divider/Divider';
import { FormElement } from 'components/Form';
import { Title, Info } from 'components/Text';
import { BLACK } from 'constants/colors';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import useGetValidationString from 'hooks/language/useGetValidationString';
import isValidEmail from 'utils/text/isValidEmail';
import useStyles from '../ProfileStyles';
const idSuffix = nanoid();
export default function EmailDialog({
  open,
  onClose,
  fieldName
}) {
  const {
    t
  } = useTranslation();
  const {
    customer,
    setCustomer,
    isBusinessCustomer
  } = useCurrentCustomer();
  const {
    dialog,
    dialogTitle,
    closeIcon,
    submitButton,
    textField,
    emailInfo
  } = useStyles();
  const {
    register,
    getValues,
    handleSubmit,
    watch
  } = useForm();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    emailFormat,
    confirmEmailFormat
  } = useGetValidationString();
  const [isLoading, setIsLoading] = useState(false);
  const submitEmail = async () => {
    if (customer) {
      try {
        setIsLoading(true);
        const newEmail = getValues('email');
        const customerBody = {
          ...customer,
          [fieldName]: newEmail
        };
        if (customer.id) {
          const customerObject = await CustomerService.editCustomer({
            customerId: customer.id,
            body: customerBody
          });
          setCustomer(customerObject);
        } else {
          console.error('Could not find customer id', customer.id);
        }
      } catch (e) {
        if (!axios.isCancel(e)) enqueueSnackbar(t('error.edit_email'), {
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
        onClose();
      }
    }
  };
  const showError = input => {
    const value = getValues(input);
    if (value) return !isValidEmail(value);
    return false;
  };
  const emailsAreIdentical = watch('email') && watch('email').toLowerCase() === watch('confirmEmail').toLowerCase();
  const disabledValue = !isValidEmail(watch('email')) || !isValidEmail(watch('confirmEmail')) || !emailsAreIdentical;
  return /*#__PURE__*/React.createElement(Dialog, {
    "aria-labelledby": "email-dialog-form",
    open: open,
    onClose: onClose
  }, /*#__PURE__*/React.createElement(DialogTitle, {
    id: "email-dialog-form"
  }, /*#__PURE__*/React.createElement("div", {
    className: dialogTitle
  }, /*#__PURE__*/React.createElement(Title, {
    component: "h1"
  }, t('profile.edit_email')), /*#__PURE__*/React.createElement(SecondaryButton, {
    className: closeIcon,
    ariaLabel: "close dialog",
    onClick: onClose
  }, /*#__PURE__*/React.createElement(CloseIcon, {
    htmlColor: BLACK
  })))), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit(submitEmail)
  }, /*#__PURE__*/React.createElement(DialogContent, {
    className: dialog
  }, /*#__PURE__*/React.createElement(Box, {
    className: emailInfo
  }, /*#__PURE__*/React.createElement(Info, null, t('profile.current_email'), ":"), isBusinessCustomer ? customer?.contactEmail : customer?.email), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.new_email'),
    childId: `email_${idSuffix}`
  }, /*#__PURE__*/React.createElement("div", {
    className: textField
  }, /*#__PURE__*/React.createElement(TextField, {
    name: "email",
    className: textField,
    inputRef: register,
    variant: "outlined",
    fullWidth: true,
    value: getValues('email'),
    error: showError('email'),
    helperText: showError('email') ? emailFormat : '',
    inputProps: {
      id: `email_${idSuffix}`
    },
    placeholder: t('profile.email')
  }))), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.confirm_new_email'),
    childId: `confirmEmail_${idSuffix}`
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(TextField, {
    name: "confirmEmail",
    className: textField,
    inputRef: register,
    variant: "outlined",
    fullWidth: true,
    autoComplete: "off",
    value: getValues('confirmEmail'),
    error: showError('confirmEmail'),
    helperText: showError('confirmEmail') ? confirmEmailFormat : '',
    inputProps: {
      id: `confirmEmail_${idSuffix}`
    },
    placeholder: t('profile.confirm_email')
  })))), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement("div", {
    className: submitButton
  }, /*#__PURE__*/React.createElement(PrimaryButton, {
    ariaLabel: "submit email",
    onClick: submitEmail,
    disabled: disabledValue,
    loading: isLoading
  }, t('buttons.save_edits'))))));
}